/** Item detail: stock by location, receive form, ledger. */
import { useNavigate, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { Attachments } from "@/features/documents/Attachments";
import { type ReorderPolicy, useReorderPolicies } from "@/features/admin/reference/queries";
import { ItemDialog, ReorderPolicyDialog } from "@/features/inventory/ItemDialogs";
import {
  type Transaction,
  useItem,
  useItemTransactions,
  useLocations,
  useReceiveStock,
} from "@/features/inventory/queries";
import { DataTable } from "@/shared/components/data-table";
import { TableToolbar, type TableFilterDefinition } from "@/shared/components/table-toolbar";
import { useListState } from "@/shared/lib/list-state";
import {
  Details,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
} from "@/shared/components/page";
import { formatDateTime, formatMoney, formatQuantity } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { Field, Input, Select } from "@/shared/ui/form";
import { Badge, Table, Td, Th, Tr } from "@/shared/ui/primitives";

/** Inline receipt form. */
export function ReceiveForm({ itemId }: { itemId: string }): ReactElement {
  const { t } = useTranslation();
  const receive = useReceiveStock();
  const locations = useLocations();
  const [locationId, setLocationId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [reference, setReference] = useState("");
  const [done, setDone] = useState(false);
  const fieldErrors = receive.error instanceof ApiError ? receive.error.fieldErrors() : {};

  const submit = (e: { preventDefault(): void }): void => {
    e.preventDefault();
    setDone(false);
    receive.mutate(
      {
        item_id: itemId,
        location_id: locationId,
        quantity: Number(quantity),
        unit_cost: unitCost ? Number(unitCost) : null,
        reference,
        reason: "",
        lot_no: "",
        serial_no: "",
      },
      {
        onSuccess: () => {
          setQuantity("");
          setUnitCost("");
          setReference("");
          setDone(true);
        },
      },
    );
  };

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 items-end gap-3 rounded-md border p-3 sm:flex sm:flex-wrap"
    >
      <Field id="rc-location" label={t("inventory.location")} error={fieldErrors.location_id}>
        <Select
          id="rc-location"
          className="w-full sm:w-56"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          required
        >
          <option value="">—</option>
          {(locations.data ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.code} · {l.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="rc-qty" label={t("app.quantity")} error={fieldErrors.quantity}>
        <Input
          id="rc-qty"
          type="number"
          step="any"
          min="0"
          className="w-full sm:w-28"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          required
        />
      </Field>
      <Field id="rc-cost" label={t("inventory.unitCost")} error={fieldErrors.unit_cost}>
        <Input
          id="rc-cost"
          type="number"
          step="any"
          min="0"
          className="w-full sm:w-28"
          value={unitCost}
          onChange={(e) => setUnitCost(e.target.value)}
        />
      </Field>
      <Field id="rc-ref" label="Ref">
        <Input
          id="rc-ref"
          className="w-full sm:w-40"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      </Field>
      <Button type="submit" disabled={receive.isPending || !locationId || !quantity}>
        {t("inventory.receive")}
      </Button>
      {receive.isError && Object.keys(fieldErrors).length === 0 ? (
        <p role="alert" className="w-full text-sm text-destructive">
          {errorMessage(receive.error)}
        </p>
      ) : null}
      {done ? (
        <p role="status" className="w-full text-sm text-emerald-700">
          {t("inventory.received")}
        </p>
      ) : null}
    </form>
  );
}

/** Item detail page. */
export function ItemDetailPage(): ReactElement {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { itemId = "" } = useParams({ strict: false });
  const { can } = useAuth();
  const item = useItem(itemId);
  const list = useListState(".");
  const ledger = useItemTransactions(itemId, {
    type: list.search.type,
    since: list.search.due_after,
    until: list.search.due_before,
  });
  const ledgerFilters: TableFilterDefinition[] = [
    {
      id: "type",
      label: t("warehouse.movement"),
      kind: "multi",
      prominent: true,
      value: list.search.type,
      options: [
        "receipt",
        "issue",
        "return",
        "transfer",
        "adjustment",
        "count",
        "scrap",
        "checkout",
        "checkin",
      ].map((value) => ({ value, label: t(`warehouse.movementType.${value}`) })),
      onChange: (value) => list.update({ type: typeof value === "string" ? value : undefined }),
      onRemove: () => list.update({ type: undefined }),
    },
    ...(["due_after", "due_before"] as const).map((key): TableFilterDefinition => ({
      id: key,
      label: t(key === "due_after" ? "warehouse.dateFrom" : "warehouse.dateTo"),
      kind: "date",
      boundary: key === "due_after" ? "start" : "end",
      value: list.search[key],
      onChange: (value) => list.update({ [key]: typeof value === "string" ? value : undefined }),
      onRemove: () => list.update({ [key]: undefined }),
    })),
  ];
  const policies = useReorderPolicies(itemId);
  const [dialog, setDialog] = useState<
    { kind: "edit" } | { kind: "policy"; policy?: ReorderPolicy } | null
  >(null);

  const columns: ColumnDef<Transaction>[] = [
    { id: "at", header: t("app.created"), cell: (c) => formatDateTime(c.row.original.occurred_at) },
    {
      id: "type",
      header: t("app.type"),
      cell: (c) => <Badge tone="outline">{c.row.original.type}</Badge>,
    },
    {
      id: "qty",
      header: t("app.quantity"),
      cell: (c) => <span className="tabular-nums">{formatQuantity(c.row.original.quantity)}</span>,
    },
    {
      id: "cost",
      header: t("inventory.unitCost"),
      cell: (c) => <span className="tabular-nums">{formatQuantity(c.row.original.unit_cost)}</span>,
    },
    {
      id: "ref",
      header: "Ref",
      cell: (c) => c.row.original.reference || c.row.original.reason || "—",
    },
  ];

  if (item.isPending) {
    return <LoadingState />;
  }
  if (item.isError) {
    return <ErrorState error={item.error} onRetry={() => void item.refetch()} />;
  }
  const it = item.data;
  return (
    <div>
      <PageHeader
        title={`${it.sku} · ${it.name}`}
        subtitle={
          <Badge tone="outline">{t(`inventory.kinds.${it.kind}`, { defaultValue: it.kind })}</Badge>
        }
        actions={
          <>
            {can("inventory.item.manage") ? (
              <Button variant="outline" onClick={() => setDialog({ kind: "edit" })}>
                {t("app.edit")}
              </Button>
            ) : null}
            {can("inventory.reorder.manage") ? (
              <Button variant="outline" onClick={() => setDialog({ kind: "policy" })}>
                {t("inventory.addPolicy")}
              </Button>
            ) : null}
            {can("inventory.stock.view") ? (
              <Button
                variant="outline"
                onClick={() =>
                  void navigate({
                    to: "/warehouse",
                    search: {
                      tab: "stock",
                      item_id: it.id,
                      location_id: it.stock[0]?.location_id,
                    },
                  })
                }
              >
                {t("warehouse.viewItemStock")}
              </Button>
            ) : null}
          </>
        }
      />
      {dialog?.kind === "edit" ? <ItemDialog item={it} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "policy" ? (
        <ReorderPolicyDialog item={it} policy={dialog.policy} onClose={() => setDialog(null)} />
      ) : null}
      <Details
        items={[
          { label: t("inventory.onHand"), value: `${formatQuantity(it.total_on_hand)} ${it.uom}` },
          { label: t("inventory.unitCost"), value: formatQuantity(it.standard_cost) },
          { label: "Costing", value: it.costing },
          { label: "Barcode", value: it.barcode || "—" },
          { label: "MPN", value: it.manufacturer_part_no || "—" },
          { label: t("app.description"), value: it.description || "—" },
        ]}
      />
      <h2 className="mb-2 mt-6 text-sm font-semibold">{t("inventory.stockByLocation")}</h2>
      {it.stock.length === 0 ? (
        <EmptyState message={t("inventory.noStock")} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t("inventory.location")}</Th>
              <Th>{t("inventory.onHand")}</Th>
              <Th>{t("inventory.reserved")}</Th>
              <Th>{t("inventory.available")}</Th>
              <Th>{t("inventory.value")}</Th>
            </tr>
          </thead>
          <tbody>
            {it.stock.map((s) => (
              <Tr key={s.location_id}>
                <Td>
                  {s.location_code}{" "}
                  <span className="text-xs text-muted-foreground">{s.location_path}</span>
                  {s.bin ? (
                    <span className="ms-1 text-xs text-muted-foreground">[{s.bin}]</span>
                  ) : null}
                </Td>
                <Td className="tabular-nums">{formatQuantity(s.on_hand)}</Td>
                <Td className="tabular-nums">{formatQuantity(s.reserved)}</Td>
                <Td className="tabular-nums">{formatQuantity(s.available)}</Td>
                <Td className="tabular-nums">{formatMoney(s.value, i18n.language)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {can("inventory.transaction.receive") ? (
        <div className="mt-4">
          <ReceiveForm itemId={it.id} />
        </div>
      ) : null}
      {policies.data && policies.data.length > 0 ? (
        <>
          <h2 className="mb-2 mt-6 text-sm font-semibold">{t("inventory.reorderPolicies")}</h2>
          <Table>
            <thead>
              <tr>
                <Th>{t("inventory.location")}</Th>
                <Th>{t("inventory.minQty")}</Th>
                <Th>{t("inventory.maxQty")}</Th>
                <Th>{t("inventory.leadTime")}</Th>
                <Th>{t("app.status")}</Th>
                {can("inventory.reorder.manage") ? <Th /> : null}
              </tr>
            </thead>
            <tbody>
              {policies.data.map((p) => (
                <Tr key={p.id}>
                  <Td className="font-mono text-xs">{p.location_id.slice(0, 8)}…</Td>
                  <Td className="tabular-nums">{formatQuantity(p.min_qty)}</Td>
                  <Td className="tabular-nums">{formatQuantity(p.max_qty)}</Td>
                  <Td>{p.lead_time_days ?? "—"}</Td>
                  <Td>
                    {p.is_active ? (
                      <Badge tone="success">{t("app.active")}</Badge>
                    ) : (
                      <Badge>{t("app.inactive")}</Badge>
                    )}
                  </Td>
                  {can("inventory.reorder.manage") ? (
                    <Td className="text-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDialog({ kind: "policy", policy: p })}
                      >
                        {t("app.edit")}
                      </Button>
                    </Td>
                  ) : null}
                </Tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : null}
      <h2 className="mb-2 mt-6 text-sm font-semibold">{t("workOrders.history")}</h2>
      <TableToolbar
        filters={ledgerFilters}
        onReset={() =>
          list.update({ type: undefined, due_after: undefined, due_before: undefined })
        }
      />
      {ledger.query.isPending ? (
        <LoadingState />
      ) : ledger.query.isError ? (
        <ErrorState error={ledger.query.error} />
      ) : (
        <DataTable
          columns={columns}
          data={ledger.items}
          minWidth={850}
          getRowId={(r) => r.id}
          total={ledger.total}
          hasMore={ledger.hasMore}
          onLoadMore={ledger.loadMore}
          isLoadingMore={ledger.isLoadingMore}
        />
      )}
      <div className="mt-6">
        <Attachments entityType="item" entityId={it.id} />
      </div>
    </div>
  );
}
